import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Wallet, Plus, Edit, Trash, RefreshCw, Save, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { validateAdvanceInput } from '../payroll/core/configRules';
import { deleteAdvance, loadAdvanceData, saveAdvance } from '../payroll/data/configActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage } from '../payroll/data/permissions';
import { parseSheetNumber } from '../payroll/data/sheetFormat';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, Field, FilterField, IconAction, ListToolbar, MessageList, Modal, PageShell,
    NumberInput, Pagination, SearchSelect, SortableTh, Spinner, StatCards, buttonPrimary, buttonSecondary, employeeOptions, formatDay, formatMoney,
    fromInputDate, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, toInputDate, usePagination, useSortedRows,
} from '../payroll/components/ui';

const STATUS = {
    CHUA_SU_DUNG: { label: 'Chưa khấu trừ', tone: 'yellow' },
    DA_SNAPSHOT: { label: 'Đã khấu trừ', tone: 'green' },
    HUY: { label: 'Hủy', tone: 'gray' },
};

function AdvanceForm({ initial, isNew, employees, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const amount = parseSheetNumber(form.amount, null);
    const advance = { ...form, NGAY_UNG: fromInputDate(form.date), SO_TIEN: amount };
    const errors = form.amount && Number.isNaN(amount) ? ['Số tiền phải là số.'] : validateAdvanceInput(advance);

    const handleSave = async () => {
        try {
            setSaving(true);
            await saveAdvance(advance, { isNew });
            toast.success(isNew ? 'Đã tạo phiếu ứng' : 'Đã cập nhật phiếu ứng');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={isNew ? 'Tạo phiếu ứng lương' : 'Cập nhật phiếu ứng lương'} subtitle="Phiếu được trừ vào kỳ lương của tháng chứa ngày ứng" onClose={onClose} disabled={saving}
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Hủy</button>
                    <button className={buttonPrimary} onClick={handleSave} disabled={saving || errors.length > 0}><Save className="w-4 h-4" />{saving ? 'Đang lưu...' : 'Lưu'}</button>
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Nhân sự" required>
                    <SearchSelect options={employeeOptions(employees)} value={form.NHAN_SU_ID} onChange={(value) => setForm((prev) => ({ ...prev, NHAN_SU_ID: value }))} placeholder="Tìm tên hoặc Số HĐ..." />
                </Field>
                <div className="space-y-4">
                    <Field label="Ngày ứng" required>
                        <input type="date" className={inputClass} value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} />
                    </Field>
                    <Field label="Số tiền" required>
                        <NumberInput value={form.amount} placeholder="Ví dụ 2.000.000" onChange={(next) => setForm((prev) => ({ ...prev, amount: next }))} />
                    </Field>
                    <Field label="Ghi chú">
                        <textarea rows={3} className={inputClass} value={form.GHI_CHU} onChange={(e) => setForm((prev) => ({ ...prev, GHI_CHU: e.target.value }))} />
                    </Field>
                </div>
            </div>
            {form.amount && errors.length > 0 && <div className="mt-4"><MessageList items={errors} /></div>}
        </Modal>
    );
}

const UngLuong = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [month, setMonth] = useState('ALL');
    const [year, setYear] = useState('ALL');
    const [status, setStatus] = useState('ALL');
    const [form, setForm] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const canView = canViewPayrollPage(PAYROLL_PATHS.advances);
    const canManage = canManagePayroll();

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setData(await loadAdvanceData());
        } catch (error) {
            console.error('Lỗi tải phiếu ứng lương:', error);
            toast.error('Không tải được phiếu ứng lương');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const employeeBySoHd = useMemo(() => new Map((data?.employees ?? []).map((employee) => [employee.SO_HD, employee])), [data]);
    const years = useMemo(() => [...new Set((data?.advances ?? []).map((row) => row.NGAY_UNG?.getFullYear()).filter(Boolean))].sort((a, b) => b - a), [data]);
    const filtered = useMemo(() => (data?.advances ?? [])
        .map((advance) => ({ ...advance, employee: employeeBySoHd.get(advance.NHAN_SU_ID) }))
        .filter((row) => (month === 'ALL' || row.NGAY_UNG?.getMonth() + 1 === Number(month))
            && (year === 'ALL' || row.NGAY_UNG?.getFullYear() === Number(year))
            && (status === 'ALL' || row.TRANG_THAI === status)
            && matchesSearch(`${row.MA_PHIEU} ${row.NHAN_SU_ID} ${row.employee?.HO_TEN} ${row.GHI_CHU}`, search)), [data, employeeBySoHd, month, year, status, search]);
    const accessors = useMemo(() => ({
        code: (row) => row.MA_PHIEU, employee: (row) => row.employee?.HO_TEN ?? row.NHAN_SU_ID, date: (row) => row.NGAY_UNG,
        amount: (row) => row.SO_TIEN ?? 0, status: (row) => row.TRANG_THAI, creator: (row) => row.NGUOI_TAO,
    }), []);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors, { key: 'date', direction: 'desc' });
    const { pageRows, pagination } = usePagination(sorted);

    if (!canView) return <AccessDenied />;

    const valid = filtered.filter((row) => row.TRANG_THAI !== 'HUY');
    const openForm = (advance, isNew) => setForm({
        isNew,
        initial: {
            MA_PHIEU: advance.MA_PHIEU || '',
            NHAN_SU_ID: advance.NHAN_SU_ID || '',
            date: toInputDate(advance.NGAY_UNG || new Date()),
            amount: advance.SO_TIEN ? String(advance.SO_TIEN) : '',
            GHI_CHU: advance.GHI_CHU || '',
            TRANG_THAI: advance.TRANG_THAI || 'CHUA_SU_DUNG',
        },
    });

    const actions = (row) => {
        if (!canManage) return null;
        if (row.TRANG_THAI === 'DA_SNAPSHOT') return <span className="inline-flex items-center gap-1 text-xs text-gray-500" title="Đã trừ vào kỳ lương đã duyệt"><Lock className="w-3.5 h-3.5" />Đã khóa</span>;
        return (
            <div className="flex justify-center gap-1">
                <IconAction icon={Edit} title="Sửa phiếu" onClick={() => openForm(row, false)} />
                <IconAction icon={Trash} title="Xóa phiếu" tone="red" onClick={() => setDeleteTarget(row)} />
            </div>
        );
    };

    return (
        <PageShell icon={Wallet} title="Phiếu ứng lương" subtitle="Theo dõi khoản ứng và trạng thái đã khấu trừ vào từng kỳ lương"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    {canManage && <button className={buttonPrimary} onClick={() => openForm({}, true)} disabled={!data}><Plus className="w-4 h-4" />Tạo phiếu ứng</button>}
                </>
            )}>
            <StatCards active={status} onSelect={setStatus} cards={[
                { key: 'total', label: 'Tổng số phiếu', value: filtered.length, tone: 'indigo', caption: 'Trong phạm vi đang lọc', filterValue: 'ALL' },
                { key: 'amount', label: 'Tổng tiền ứng', value: `${formatMoney(valid.reduce((sum, row) => sum + (row.SO_TIEN || 0), 0))} đ`, tone: 'green' },
                { key: 'employees', label: 'Nhân sự đã ứng', value: new Set(valid.map((row) => row.NHAN_SU_ID)).size, tone: 'yellow' },
                { key: 'snapshot', label: 'Đã khấu trừ', value: filtered.filter((row) => row.TRANG_THAI === 'DA_SNAPSHOT').length, tone: 'purple', caption: 'Đã khóa theo kỳ lương', filterValue: 'DA_SNAPSHOT' },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm mã phiếu, nhân sự, số hợp đồng, ghi chú..."
                filterCount={[month, year, status].filter((value) => value !== 'ALL').length}
                onClearFilters={() => { setMonth('ALL'); setYear('ALL'); setStatus('ALL'); setSearch(''); }}
                filters={(
                    <>
                        <FilterField label="Tháng">
                            <select className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)}>
                                <option value="ALL">Tất cả tháng</option>
                                {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>Tháng {index + 1}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Năm">
                            <select className={inputClass} value={year} onChange={(e) => setYear(e.target.value)}>
                                <option value="ALL">Tất cả năm</option>
                                {years.map((item) => <option key={item} value={item}>{item}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Trạng thái">
                            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="ALL">Tất cả trạng thái</option>
                                {Object.entries(STATUS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
                            </select>
                        </FilterField>
                    </>
                )} />
            {loading || !data ? <Spinner /> : sorted.length === 0 ? (
                <EmptyState icon={Wallet} title="Chưa có phiếu ứng phù hợp">Tạo phiếu ứng để khoản ứng được tự trừ vào bảng lương của tháng.</EmptyState>
            ) : (
                <>
                    <div className="space-y-3 md:hidden">
                        {pageRows.map((row) => (
                            <div key={row.MA_PHIEU} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start gap-2">
                                    <div><p className="font-medium text-gray-900">{row.employee?.HO_TEN || row.NHAN_SU_ID}</p><p className="text-xs text-gray-500">{row.NHAN_SU_ID} · {formatDay(row.NGAY_UNG)}</p></div>
                                    <Badge tone={(STATUS[row.TRANG_THAI] || STATUS.CHUA_SU_DUNG).tone}>{(STATUS[row.TRANG_THAI] || STATUS.CHUA_SU_DUNG).label}</Badge>
                                </div>
                                <p className="text-lg font-bold text-gray-900 mt-2">{formatMoney(row.SO_TIEN)} đ</p>
                                {row.GHI_CHU && <p className="text-sm text-gray-600">{row.GHI_CHU}</p>}
                                <div className="flex justify-end mt-2">{actions(row)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <SortableTh label="Phiếu" sortKey="code" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Nhân sự" sortKey="employee" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Ngày ứng" sortKey="date" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Số tiền" sortKey="amount" sort={sort} onSort={toggleSort} align="right" />
                                    <SortableTh label="Trạng thái" sortKey="status" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Người tạo" sortKey="creator" sort={sort} onSort={toggleSort} />
                                    <th className={thClass}>Ghi chú</th>
                                    <th className={`${thClass} text-center`}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((row) => {
                                    const statusInfo = STATUS[row.TRANG_THAI] || STATUS.CHUA_SU_DUNG;
                                    return (
                                        <tr key={row.MA_PHIEU} className="hover:bg-gray-50 transition-colors">
                                            <td className={`${tdClass} font-medium text-indigo-600 whitespace-nowrap`}>{row.MA_PHIEU}</td>
                                            <td className={tdClass}><div className="font-medium text-gray-900">{row.employee?.HO_TEN || row.NHAN_SU_ID}</div><div className="text-xs text-gray-500">{row.NHAN_SU_ID}</div></td>
                                            <td className={`${tdClass} whitespace-nowrap`}>{formatDay(row.NGAY_UNG)}</td>
                                            <td className={`${tdClass} text-right font-semibold text-gray-900 whitespace-nowrap`}>{formatMoney(row.SO_TIEN)} đ</td>
                                            <td className={tdClass}><Badge tone={statusInfo.tone}>{statusInfo.label}</Badge>{row.BANG_CONG_LUONG_ID && <div className="text-xs text-gray-500 mt-1">{row.BANG_CONG_LUONG_ID}</div>}</td>
                                            <td className={tdClass}>{row.NGUOI_TAO}</td>
                                            <td className={`${tdClass} max-w-xs truncate`} title={row.GHI_CHU}>{row.GHI_CHU}</td>
                                            <td className={tdClass}>{actions(row)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination pagination={pagination} unit="phiếu" />
                </>
            )}
            {form && <AdvanceForm {...form} employees={data.employees} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
            {deleteTarget && (
                <ConfirmDialog title="Xác nhận xóa phiếu ứng" onClose={() => setDeleteTarget(null)} confirmLabel="Xóa phiếu"
                    message={<>Xóa phiếu ứng <b>{formatMoney(deleteTarget.SO_TIEN)} đ</b> ngày {formatDay(deleteTarget.NGAY_UNG)} của <b>{deleteTarget.employee?.HO_TEN || deleteTarget.NHAN_SU_ID}</b>?</>}
                    onConfirm={async () => {
                        try {
                            await deleteAdvance(deleteTarget);
                            toast.success('Đã xóa phiếu ứng');
                            setDeleteTarget(null);
                            reload();
                        } catch (error) {
                            toast.error(error.message);
                        }
                    }} />
            )}
        </PageShell>
    );
};

export default UngLuong;
